import WebentwicklungKalkulator from './components/WebentwicklungKalkulator'
import SimplifiedNav from './components/SimplifiedNav'
import WebsiteFragebogen from './components/WebsiteFragebogen'
import QuoteDashboard from './components/QuoteDashboard'
import QuoteDetail from './components/QuoteDetail'
import QuoteAccepted from './components/QuoteAccepted'
import CustomerDashboard from './components/CustomerDashboard'
import CustomerOrderDetail from './components/CustomerOrderDetail'
import Login from './components/Login'
import CustomerLogin from './components/CustomerLogin'
import UpdateUserProfile from './components/UpdateUserProfile'
import ModuleSidebar from './components/ModuleSidebar'
import ScrollitellingDashboard from './components/ScrollitellingDashboard'
import InvoiceDashboard from './components/InvoiceDashboard'
import TemplateManagement from './components/TemplateManagement'
import TeamMemberTasks from './components/TeamMemberTasks'
import QuestionnaireFillout from './components/QuestionnaireFillout'
import { InitializeTemplates } from './components/InitializeTemplates'
import { useAuth } from './contexts/AuthContext'
import { questionnaireService } from './services/questionnaireService'

function App() {
  const { user, userProfile, loading } = useAuth();
  const pathname = window.location.pathname;
  
  // Module navigation handler (shared across admin routes)
  const handleModuleChange = (module: 'quotes' | 'scrollitelling' | 'rechnungen' | 'templates') => {
    if (module === 'quotes') window.location.href = '/quotes';
    else if (module === 'scrollitelling') window.location.href = '/scrollitelling';
    else if (module === 'rechnungen') window.location.href = '/rechnungen';
    else if (module === 'templates') window.location.href = '/templates';
  };
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }
  
  // Auto-redirect authenticated users to their default dashboard
  // Only if they come from login (not from explicit navigation)
  if (user && userProfile && pathname === '/' && window.location.search === '') {
    // Check if this is a fresh login (coming from CustomerLogin)
    const isFromLogin = document.referrer.includes('/login') || !document.referrer;
    
    if (isFromLogin) {
      // Team members: Check for open questionnaire tasks FIRST
      if (userProfile.isTeamMember && userProfile.role !== 'admin') {
        // Async redirect to open tasks if any exist
        (async () => {
          try {
            const assignments = await questionnaireService.getAssignmentsForUser(user.email || '');
            const now = new Date();
            const openTasks = assignments.filter(a => 
              a.status !== 'submitted' && 
              new Date(a.deadline) > now
            );
            
            if (openTasks.length > 0) {
              // Redirect to first open task
              window.location.href = `/questionnaire/${openTasks[0].id}`;
            } else {
              // No open tasks, go to their dashboard
              window.location.href = '/my-tasks';
            }
          } catch (error) {
            console.error('Error checking tasks:', error);
            // Fallback to task dashboard
            window.location.href = '/my-tasks';
          }
        })();
        return null;
      }
      
      // Customers auto-redirect to their orders
      if (userProfile.role === 'customer') {
        window.location.href = '/my-orders';
        return null;
      }
    }
  }
  
  // Admin-only user profile update tool
  if (pathname === '/update-users') {
    if (!user || userProfile?.role !== 'admin') {
      return <Login />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <UpdateUserProfile />
        </div>
      </div>
    );
  }
  
  // Admin-only routes require admin authentication
  if (pathname === '/quotes' || pathname.startsWith('/quotes/')) {
    if (!user || userProfile?.role !== 'admin') {
      return <Login />;
    }
    
    if (pathname === '/quotes') {
      return (
        <div className="App">
          <SimplifiedNav />
          <ModuleSidebar currentModule="quotes" onModuleChange={handleModuleChange} />
          <div className="ml-20 pt-14">
            <QuoteDashboard />
          </div>
        </div>
      );
    }
    
    if (pathname.startsWith('/quotes/')) {
      return (
        <div className="App">
          <SimplifiedNav />
          <ModuleSidebar currentModule="quotes" onModuleChange={handleModuleChange} />
          <div className="ml-20 pt-14">
            <QuoteDetail />
          </div>
        </div>
      );
    }
  }
  
  // Quote acceptance confirmation page
  if (pathname === '/quote-accepted') {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return <QuoteAccepted />;
  }
  
  // Customer dashboard routes
  if (pathname === '/my-orders') {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <CustomerDashboard />
        </div>
      </div>
    );
  }
  
  if (pathname.startsWith('/my-orders/')) {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <CustomerOrderDetail />
        </div>
      </div>
    );
  }
  
  // Kickoff fragebogen - only accessible with quoteId
  if (pathname === '/kickoff') {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <WebsiteFragebogen />
        </div>
      </div>
    );
  }
  
  // Questionnaire fillout route (for team members)
  if (pathname.startsWith('/questionnaire/')) {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    
    const assignmentId = pathname.split('/')[2];
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <QuestionnaireFillout assignmentId={assignmentId} />
        </div>
      </div>
    );
  }
  
  // Scroll-i-telling admin dashboard
  if (pathname === '/scrollitelling') {
    if (!user || userProfile?.role !== 'admin') {
      return <Login />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <ModuleSidebar currentModule="scrollitelling" onModuleChange={handleModuleChange} />
        <div className="ml-20 pt-14">
          <ScrollitellingDashboard />
        </div>
        <InitializeTemplates />
      </div>
    );
  }
  
  // Template management admin dashboard
  if (pathname === '/templates') {
    if (!user || userProfile?.role !== 'admin') {
      return <Login />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <ModuleSidebar currentModule="templates" onModuleChange={handleModuleChange} />
        <div className="ml-20 pt-14">
          <TemplateManagement />
        </div>
      </div>
    );
  }
  
  // Rechnungen admin dashboard
  if (pathname === '/rechnungen' || pathname.startsWith('/rechnungen/')) {
    if (!user || userProfile?.role !== 'admin') {
      return <Login />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <ModuleSidebar currentModule="rechnungen" onModuleChange={handleModuleChange} />
        <div className="ml-20 pt-14">
          <InvoiceDashboard />
        </div>
      </div>
    );
  }
  
  // Team member tasks (for team members with @chimperator-live.de/@imwizemann.de)
  if (pathname === '/my-tasks') {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <TeamMemberTasks />
        </div>
      </div>
    );
  }
  
  // Calculator route - only for admins or for creating new orders
  if (pathname === '/' || pathname === '/calculator') {
    if (!user) {
      return <CustomerLogin onSuccess={() => window.location.reload()} />;
    }
    return (
      <div className="App">
        <SimplifiedNav />
        <div className="pt-14">
          <WebentwicklungKalkulator />
        </div>
        <InitializeTemplates />
      </div>
    );
  }
  
  // Fallback - redirect to appropriate dashboard
  if (user && userProfile) {
    if (userProfile.role === 'customer') {
      window.location.href = '/my-orders';
    } else {
      window.location.href = '/quotes';
    }
  } else {
    window.location.href = '/';
  }
  
  return null;
}

export default App
